import { createContext, useState, useEffect } from 'react';
import { Cart, Group } from '@shopify/app-bridge/actions';
import Api from '../helpers/api';
import { personalizationIdPropertyName } from '../globals';

import * as Sentry from '@sentry/react';

export const CartContext = createContext({
    cartStatus: null,
    lineItems: null,
    cart: null,
});

export const CartContextProvider = ({ children, onlyAllowOneEngraving }) => {
    const [firstLoad, setFirstLoad] = useState(true);
    const [cartStatus, setCartStatus] = useState(null);
    const [lineItems, setLineItems] = useState(null);
    const [productLineItems, setProductLineItems] = useState(null);
    const [cart, setCart] = useState(null);

    const [engravingChargeProduct, setEngravingChargeProduct] = useState(null);

    useEffect(() => {
        doSetup();
    }, []);

    const doSetup = async () => {
        validateAvailableFeatures().then((cartValidationStatus) => {
            setCartStatus(cartValidationStatus);
        });

        Api.getEngravingChargeProduct().then((response) => {
            setEngravingChargeProduct(response.data);
        });
    };

    useEffect(() => {
        if (engravingChargeProduct != null && cartStatus == 'available') {
            subscribeToCartUpdates().then(() => {
                setFirstLoad(false);
                setCartStatus('ready');
            });
        }
    }, [cartStatus, engravingChargeProduct]);

    const validateAvailableFeatures = () => {
        return new Promise((resolve, reject) => {
            window.app
                .featuresAvailable(Group.Cart)
                .then((state) => {
                    window.app.error((data) => {
                        console.error('[client] Error received: ', data);
                        Sentry.captureException(data);
                        reject('access-error');
                    });

                    var _ref =
                            state.Cart &&
                            state.Cart[Cart.Action.FETCH] &&
                            state.Cart[Cart.Action.SET_LINE_ITEM_PROPERTIES],
                        Dispatch = _ref.Dispatch;

                    let cartIsAvailable = false;
                    if (Dispatch) {
                        cartIsAvailable = true;
                    }

                    resolve(cartIsAvailable ? 'available' : 'unavailable');
                })
                .catch((error) => {
                    console.error('[client] General error: ', error);
                    Sentry.captureException(error);
                    reject('access-error');
                });
        });
    };

    const subscribeToCartUpdates = () => {
        return new Promise((resolve, reject) => {
            const cart = Cart.create(app);
            setCart(cart);

            let unsubscriber = cart.subscribe(Cart.Action.UPDATE, (payload) => {
                const cartData = Object.keys(payload?.data?.lineItems || {}).length ? payload.data.lineItems : null;
                if (cartData) {
                    setLineItems(cartData);
                } else {
                    setLineItems([]);
                }

                if (firstLoad) {
                    // After the first load, this won't be running in the Promise
                    resolve();
                }

                unsubscriber();
            });
            cart.dispatch(Cart.Action.FETCH);
        });
    };

    const trackerLinkPropertyName = (trackedItem, index) => {
        return trackedItem.title + ' (Line Item Number-' + (index + 1) + ')';
    };

    const getPersonalizationIdProperty = (lineItem) => {
        if (!lineItem.properties || !lineItem.properties.length) {
            return false;
        }
        return lineItem.properties.find((property) => property.name == personalizationIdPropertyName);
    };

    const removeLineItems = (lineItemList) => {
        if (lineItems) {
            // Because we're removing based on index in line items, we need to remove them from the bottom up.
            let removalIndeces = lineItemList.map((lineItem) => {
                return lineItems.indexOf(lineItem);
            });
            removalIndeces.sort();
            removalIndeces.reverse();
            removalIndeces.forEach((index) => {
                cart.dispatch(Cart.Action.REMOVE_LINE_ITEM, {
                    index: index,
                });
            });
        }
    };

    const getCaseInsensitivePropertyValue = (itemProperties, keyString) => {
        let matchingProperty = itemProperties.find(
            (property) => property.name.toLowerCase() == keyString.toLowerCase(),
        );
        if (matchingProperty !== undefined) {
            return matchingProperty.value;
        }
        return null;
    };

    const getCorrectFeeVariantForItem = (item) => {
        return engravingChargeProduct.variants.find((feeVariant) => {
            // Make sure the fee and the line item either both have "set of 2", or neither do.
            if (item.title.toLowerCase().includes('set of 2') || item.title.toLowerCase().includes('set of two')) {
                if (
                    !feeVariant.title.toLowerCase().includes('set of 2') &&
                    !feeVariant.title.toLowerCase().includes('set of two')
                ) {
                    return false;
                }
            } else {
                if (
                    feeVariant.title.toLowerCase().includes('set of 2') ||
                    feeVariant.title.toLowerCase().includes('set of two')
                ) {
                    return false;
                }
            }

            // Now compare engraving types...
            let engravingType = feeVariant.title
                .toLowerCase()
                .replace(/\s*\(set of (2|two)\)/gi, '')
                .trim();
            // This should just be "standard", "initials", "monogram", "mixed text" or "extended"

            // Hopefully the engraving type property will have one of those.
            if (
                getCaseInsensitivePropertyValue(item.properties, 'engraving type').toLowerCase().includes(engravingType)
            ) {
                return true;
            }
        });
    };

    // Make sure customised items have a corresponding personalization item, to match Zepto's behaviour
    useEffect(() => {
        // The broad approach is: if a product line item has ANY properties, we will create/update a tracker line item that points back to it, and move all the properties across.
        // The tracker item also needs a property like "Product Name (Line Item Number-1)" (see trackerLinkPropertyName()) which is how the python dax/shopify integration links them up.
        // However, while we're in a cart being edited, we can't set that and forget it, so the LineItemPropertiesSetter gives the items a unique 'Personalization ID' property to keep them linked.

        if (!lineItems) {
            return;
        }
        let _productLineItems = lineItems.filter((lineItem) => {
            return lineItem.productId && lineItem.productId != engravingChargeProduct.id;
        });
        setProductLineItems(_productLineItems);

        // First up: Delete all non-product items that have a 'Personalization ID' - we need to be able to update them with other data than just the quantity.
        let staleTrackerItems = lineItems.filter((lineItem) => {
            if (lineItem.productId && lineItem.productId != engravingChargeProduct.id) {
                // Only trackers
                return false;
            }

            let personalizationIdProperty = getPersonalizationIdProperty(lineItem);
            if (personalizationIdProperty == undefined) {
                // Only trackers with personalization IDs
                return false;
            }

            return true;
        });

        // Because the line item removal takes some time and doesn't work as a promise, if any stale trackers are removed, wait a moment before adding new/replacement trackers.
        // This avoids problems from how we remove line items based on their index/position.
        let timer = 1;
        if (staleTrackerItems.length) {
            removeLineItems(staleTrackerItems);
            timer = 500;
        }

        window.setTimeout(() => {
            _productLineItems.forEach((lineItem, indexAmongProductLines) => {
                if (!lineItem.properties?.length) {
                    return;
                }

                let personalizationIdProperty = getPersonalizationIdProperty(lineItem);
                if (!personalizationIdProperty) {
                    return;
                }

                let engravingChargeVariant = getCorrectFeeVariantForItem(lineItem);

                if (engravingChargeVariant) {
                    // Create a new tracker for the engraved product.
                    let trackerData = {
                        variantId: engravingChargeVariant.id,
                        quantity: lineItem.quantity,
                        properties: [
                            ...lineItem.properties,
                            {
                                name: '_Customization For',
                                value: trackerLinkPropertyName(lineItem, indexAmongProductLines), // Note that this is the index *among line items that are products* - and that we rewrite it every time in case of index changes!
                            },
                        ],
                    };

                    cart.dispatch(Cart.Action.ADD_LINE_ITEM, {
                        data: trackerData,
                    });
                } else {
                    // We should always be able to match a product variant of the engraving charge/fee product, so this should be corrected if it triggers.
                    console.error(
                        'No engraving charge variant found for properties ' + JSON.stringify(lineItem.properties),
                    );
                    let error = new Error(
                        'No engraving charge variant found for properties ' + JSON.stringify(lineItem.properties),
                    );
                    Sentry.captureException(error);
                }
            });
        }, timer);
    }, [lineItems]);

    return (
        <CartContext.Provider
            value={{
                cartStatus,
                lineItems,
                productLineItems,
                cart,
                engravingChargeProduct,
                onlyAllowOneEngraving,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};
